import { action, makeObservable, observable } from 'mobx';
import {
  CallPageType,
  CallStatusType,
  ICallPage,
} from '../shared/interfaces/call-page.interface';
import { RootStore } from './RootStore';
import { EModeValue } from '../services/dynamics/enums/mode.enum';
import { IAgentApi } from '../services/solgari-api/interfaces/agent.interface';
import { SolgariApiService } from '../services/solgari-api/solgari-api.service';
import { CALL_PAGE_STATUS, CALL_PAGE_TYPE } from '../shared/constants/callPage';
import { IEntity } from '../shared/interfaces/entity.interface';
import { ISession } from '@/services/sip-js.service';
import { DynRelatedToData } from '../services/dynamics/interfaces/relate-to-info.interface';

class CallStore {
  @observable
  callPage: ICallPage;

  @observable
  widgetMode: EModeValue;

  @observable
  agentsOnCall: IAgentApi[] = [];

  @observable
  conferenceExtension: string;

  @observable
  activeContact: IEntity | null;

  @observable
  contacts: IEntity[] | null;

  @observable
  wrapUpSession: ISession | null;

  @observable
  wrapUpTimer: NodeJS.Timeout | null;

  @observable
  wrapUpCounter: number | null;

  @observable
  callControls: {
    [sessionId: string]: {
      muted: boolean;
      paused: boolean;
      onHold: boolean;
    };
  };

  @observable
  callNote: {
    id?: string;
    text?: string;
    relatedToData?: DynRelatedToData;
    assignToData?: any;
    extendedSubject?: string;
  };

  constructor(public root: RootStore) {
    this.callPage = {
      page: CALL_PAGE_TYPE.HIDDEN,
      status: CALL_PAGE_STATUS.INACTIVE,
    };
    this.widgetMode = EModeValue.DOCK;
    this.conferenceExtension = SolgariApiService.generateRandomNumberString();
    this.contacts = null;
    this.wrapUpTimer = null;
    this.wrapUpCounter = null;
    this.callControls = {};
    this.activeContact = null;
    this.callNote = {
      id: '',
      text: '',
      relatedToData: null,
      assignToData: null,
    };
    makeObservable(this);
  }

  @action
  setCallPage = (data: { page?: CallPageType; status?: CallStatusType }) => {
    this.callPage = { ...this.callPage, ...data };
  };

  @action
  setWidgetMode = (mode: EModeValue) => {
    this.widgetMode = mode;
  };

  @action
  setAgentsOnCall = (data: IAgentApi[]) => {
    this.agentsOnCall = [...data];
  };

  @action
  setWrapUpSession = (session: ISession | null) => {
    this.wrapUpSession = session;
  };

  @action
  setActiveContact = (newContact: IEntity | null) => {
    this.activeContact = newContact;
  };

  @action
  clearCallData = () => {
    this.agentsOnCall = [];
    this.conferenceExtension = SolgariApiService.generateRandomNumberString();
    this.callControls = {};
    this.contacts = null;
  };

  @action
  setContacts = (newContacts: IEntity[]) => {
    this.contacts = newContacts;
  };

  @action
  getCallControls = (sessionId: string) => {
    if (!this.callControls[sessionId]) {
      this.callControls[sessionId] = {
        muted: false,
        paused: false,
        onHold: false,
      };
    }
    return this.callControls[sessionId];
  };

  @action
  setCallControls = (
    sessionId: string,
    controls: Partial<{ muted: boolean; paused: boolean; onHold: boolean }>,
  ) => {
    if (!this.callControls[sessionId]) {
      this.callControls[sessionId] = {
        muted: false,
        paused: false,
        onHold: false,
      };
    }
    this.callControls[sessionId] = {
      ...this.callControls[sessionId],
      ...controls,
    };
  };

  @action
  removeCallControls = (sessionId: string) => {
    delete this.callControls[sessionId];
  };

  @action
  setWrapUpTimer = (timer: NodeJS.Timeout | null) => {
    this.wrapUpTimer = timer;
  };

  @action
  setWrapUpCounter = (counter: number | null) => {
    this.wrapUpCounter = counter;
  };

  @action
  clearWrapUpTimer = () => {
    if (this.wrapUpTimer) {
      clearInterval(this.wrapUpTimer);
    }
    this.wrapUpTimer = null;
    this.wrapUpCounter = null;
  };

  @action
  setCallNote = (note: {
    id?: string;
    text?: string;
    relatedToData?: DynRelatedToData;
    assignToData?: any;
    extendedSubject?: string;
  }) => {
    this.callNote = note;
  };

  @action
  clearCallNote = () => {
    this.callNote = {
      id: '',
      text: '',
      relatedToData: null,
      assignToData: null,
      extendedSubject: '',
    };
  };
}

export default CallStore;
